// Ship or return, batch first. One drawer per order: scan serial after serial
// (each lands on Enter), set a count for every line that ships by count, and
// record the lot in one submit. Handhelds are keyboard wedges, so every field
// here is typed or scanned and Enter does the obvious next thing.
//
// A 409 needs_confirm is the record disagreeing with the bench (the drone is
// held by someone else, already out, or has no open out line here). The drawer
// shows what the record expected and lets the person record anyway, which
// re-sends the same body with confirm: true.
import React, { useMemo, useState } from 'react';
import DrawerShell from '../Receiving/DrawerShell';
import Alert from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import { Field, FieldRow } from '../components/ui/Field';
import Panel from '../components/ui/Panel';
import Reveal from '../components/ui/Reveal';
import Appear from '../components/ui/Appear';
import { useExiting } from '../components/ui/motion';
import { buttonClass, IconButton } from '../components/ui/Button';
import { Tooltip } from '../components/ui/Tooltip';
import { IconClose } from '../components/ui/icons';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import { businessToday } from '../utils/date-utils';
import { shipErrorMessage, shipWarnings, warningDetail } from './shipping';
import type { ShipBody, ShipDirection, Shipping, ShipResult, ShipWarning } from './shipping';

interface Props {
  order: { id: string; so_number: string };
  shipping: Shipping;
  direction: ShipDirection;
  onClose: () => void;
  onRecorded: (result: ShipResult) => void;
}

/** A line the drawer asks a count for, with the most it may take. */
interface CountRow {
  id: string;
  label: string;
  max: number;
}

const WORD = {
  out: { verb: 'Ship', title: 'Ship', date: 'Ship date', done: 'Shipped' },
  return: { verb: 'Return', title: 'Return', date: 'Return date', done: 'Returned' },
} as const;

const digits = (v: string) => v.replace(/[^0-9]/g, '');

const ShipDrawer: React.FC<Props> = ({ order, shipping, direction, onClose, onRecorded }) => {
  const api = useApi();
  const toast = useToast();
  const word = WORD[direction];

  // What each line can still take: out is what the order still owes; a return
  // is what is out and not yet back.
  const { countRows, serialRows } = useMemo(() => {
    const room = (l: Shipping['lines'][number]) => (direction === 'out' ? l.remaining : l.out - l.returned);
    const open = shipping.lines.filter((l) => room(l) > 0);
    return {
      countRows: open.filter((l) => !l.serialized).map((l): CountRow => ({ id: l.sales_order_line_id, label: l.label, max: room(l) })),
      serialRows: open.filter((l) => l.serialized).map((l) => ({ id: l.sales_order_line_id, label: l.label, left: room(l) })),
    };
  }, [shipping, direction]);

  const [scan, setScan] = useState('');
  const [serials, setSerials] = useState<string[]>([]);
  const shownSerials = useExiting(serials, (x) => x);
  const [note, setNote] = useState<string | null>(null);
  // Out pre-fills what is still owed; a return starts blank so nothing comes back by accident.
  const [counts, setCounts] = useState<Record<string, string>>(() =>
    Object.fromEntries(countRows.map((r) => [r.id, direction === 'out' ? String(r.max) : ''])),
  );
  const [tracking, setTracking] = useState('');
  const [date, setDate] = useState(() => businessToday().format('YYYY-MM-DD'));
  // What the drawer opened with, for the dirty comparison. Fixed at mount; the page remounts it per opening.
  const [opened] = useState(() => ({ counts, date }));
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<ShipWarning[] | null>(null);
  const [busy, setBusy] = useState(false);

  // Scanned serials are not tied to a line, so they count down the lines in order.
  const leftRows = useMemo(() => {
    let pool = serials.length;
    return serialRows
      .map((r) => {
        const used = Math.min(pool, r.left);
        pool -= used;
        return { ...r, left: r.left - used };
      })
      .filter((r) => r.left > 0);
  }, [serialRows, serials.length]);
  // A return counts drones by model: any drone of a leased model the customer holds, whichever serial went out.
  const expectedBack = serialRows.reduce((n, r) => n + r.left, 0);
  const noSerials = direction === 'out' && serialRows.length === 0;

  const countOf = (id: string) => Number(counts[id] || 0);
  const countTotal = countRows.reduce((n, r) => n + countOf(r.id), 0);
  const total = serials.length + countTotal;

  // Anything edited after a warning makes the warning stale.
  const touched = () => {
    setError(null);
    setWarnings(null);
  };

  const addSerial = () => {
    const s = scan.trim();
    if (!s) return;
    if (serials.some((x) => x.toLowerCase() === s.toLowerCase())) {
      setNote(`${s} is already on the list.`);
      setScan('');
      return;
    }
    setSerials((prev) => [...prev, s]);
    setScan('');
    setNote(null);
    touched();
  };

  const removeSerial = (s: string) => {
    setSerials((prev) => prev.filter((x) => x !== s));
    touched();
  };

  const send = async (confirm: boolean) => {
    const over = countRows.find((r) => countOf(r.id) > r.max);
    if (over) {
      setError(
        direction === 'out'
          ? `${over.label}: only ${over.max} still owed on this order.`
          : `${over.label}: only ${over.max} out and not yet returned.`,
      );
      return;
    }
    const body: ShipBody = {
      direction,
      shipped_on: date || undefined,
      tracking_number: tracking.trim() || undefined,
      serials,
      counts: countRows.filter((r) => countOf(r.id) > 0).map((r) => ({ sales_order_line_id: r.id, quantity: countOf(r.id) })),
      ...(confirm ? { confirm: true } : {}),
    };
    setBusy(true);
    setError(null);
    try {
      const result = (await api.post(`/orders/${order.id}/shipments`, { body })) as ShipResult;
      toast.success(`${word.done} ${total} on ${order.so_number}.`);
      onRecorded(result);
      onClose();
    } catch (err) {
      const found = shipWarnings(err);
      if (found) {
        setWarnings(found);
      } else {
        setWarnings(null);
        setError(shipErrorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const canSend = total > 0 && !busy;

  // A scanned batch, a changed count, a tracking number or a moved date is work: the shell asks before Esc or the scrim discards it.
  const dirty =
    scan.trim() !== '' ||
    serials.length > 0 ||
    tracking.trim() !== '' ||
    date !== opened.date ||
    countRows.some((r) => countOf(r.id) !== Number(opened.counts[r.id] || 0));

  const onEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (canSend && !warnings) send(false);
  };

  return (
    <DrawerShell title={`${word.title} ${order.so_number}`} onClose={onClose} dirty={dirty}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSend && !warnings) send(false);
        }}
      >
        <div>
          {!noSerials && (
          <>
          <FieldRow label="Drone serial">
            <Field
              type="text"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              value={scan}
              data-drawer-autofocus
              placeholder="Scan or type a serial, then Enter"
              onChange={(e) => {
                setScan(e.target.value);
                setNote(null);
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                addSerial();
              }}
              className="font-mono"
            />
          </FieldRow>
          <p role="status" className="mt-1 min-h-[1rem] text-xs text-on-surface-variant">
            {note ?? ''}
          </p>
          </>
          )}

          <Reveal show={serials.length > 0}>
            <ul aria-label="Serials to record" className="mt-1 flex flex-wrap gap-1.5">
              {shownSerials.map((e) => (
                <Appear as="li" key={e.key} show={!e.leaving} className="inline-flex items-center gap-0.5 rounded-full bg-surface-container-highest py-0.5 pl-3 pr-0.5 font-mono text-xs text-on-surface">
                  {e.item}
                  <Tooltip label={`Remove ${e.item}`}>
                    <IconButton label={`Remove ${e.item}`} size="sm" onClick={() => removeSerial(e.item)} className="!h-6 !w-6">
                      <IconClose className="h-3 w-3" />
                    </IconButton>
                  </Tooltip>
                </Appear>
              ))}
            </ul>
          </Reveal>

          <Reveal show={direction === 'out' && leftRows.length > 0}>
            <p className="mt-2 text-xs text-on-surface-variant">
              {leftRows.map((r, i) => (
                <span key={r.id}>
                  {i > 0 && ', '}
                  <span className="text-on-surface">{r.label}</span> still to scan <span className="tabular-nums">{r.left}</span>
                </span>
              ))}
            </p>
          </Reveal>

          <Reveal show={direction === 'return' && expectedBack > 0}>
            <p className="mt-2 text-xs text-on-surface-variant">
              <span className="tabular-nums text-on-surface">{expectedBack}</span> {expectedBack === 1 ? 'drone' : 'drones'} expected back
              {serials.length > 0 && <>, <span className="tabular-nums">{serials.length}</span> scanned</>}. Any drone of a leased model counts.
            </p>
          </Reveal>
        </div>

        {countRows.length > 0 && (
          <Panel on="panel" padding="sm" label={direction === 'out' ? 'Counts to ship' : 'Counts to return'}>
            <div className="space-y-2">
            {countRows.map((r) => (
              <label key={r.id} className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                <span className="flex min-w-0 flex-col text-sm text-on-surface sm:flex-row sm:items-baseline sm:gap-2 sm:whitespace-nowrap">
                  {r.label}
                  <span className="text-xs text-on-surface-variant tabular-nums">
                    {direction === 'out' ? `${r.max} still owed` : `${r.max} out`}
                  </span>
                </span>
                <Field
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  aria-label={`${r.label} count`}
                  value={counts[r.id] ?? ''}
                  onChange={(e) => {
                    setCounts((prev) => ({ ...prev, [r.id]: digits(e.target.value) }));
                    touched();
                  }}
                  onKeyDown={onEnter}
                  className="!w-20 shrink-0 self-end text-right tabular-nums"
                />
              </label>
            ))}
            </div>
          </Panel>
        )}

        {serialRows.length === 0 && countRows.length === 0 && (
          <EmptyState variant="line">
            {direction === 'out'
              ? 'Every line on this order has shipped.'
              : 'Nothing is out on this order. A drone you scan here asks you to confirm first.'}
          </EmptyState>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FieldRow label="Tracking number">
            <Field
              type="text"
              autoComplete="off"
              value={tracking}
              onChange={(e) => setTracking(e.target.value)}
              onKeyDown={onEnter}
              className="font-mono"
            />
          </FieldRow>
          <FieldRow label={word.date}>
            <Field type="date" value={date} onChange={(e) => setDate(e.target.value)} onKeyDown={onEnter} />
          </FieldRow>
        </div>

        <Reveal show={!!error}>
          <Alert variant="text" tone="error">{error}</Alert>
        </Reveal>

        <Reveal show={!!warnings}>
          <Alert
            tone="error"
            title={`The record disagrees with ${(warnings?.length ?? 0) === 1 ? 'this drone' : 'these drones'}.`}
            action={
              <>
                <button type="button" onClick={() => send(true)} disabled={busy} className={buttonClass('danger', 'md')}>
                  {busy ? 'Recording…' : 'Record anyway'}
                </button>
                <button type="button" onClick={() => setWarnings(null)} disabled={busy} className={buttonClass('quiet', 'md')}>
                  Back to the list
                </button>
              </>
            }
          >
            <ul className="space-y-1 text-on-surface">
              {(warnings ?? []).map((w) => (
                <li key={`${w.serial}-${w.code}`}>
                  <span className="font-mono">{w.serial}</span>
                  {' '}
                  {warningDetail(w)}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-on-surface-variant">
              {direction === 'out'
                ? 'Recording anyway saves these lines marked for reconciling and notes your override on each drone.'
                : 'Recording anyway corrects each drone\'s record and notes what it said. The drone is here.'}
            </p>
          </Alert>
        </Reveal>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className={buttonClass('quiet', 'md')}>
            Cancel
          </button>
          <button type="submit" disabled={!canSend || !!warnings} className={buttonClass('filled', 'md')}>
            {busy && !warnings ? 'Recording…' : total > 0 ? `${word.verb} ${total}` : word.verb}
          </button>
        </div>
      </form>
    </DrawerShell>
  );
};

export default ShipDrawer;
